import React from "react";
import { ModeRendererProps } from "./types";

export function VasarelyAnadolMode({ brand }: ModeRendererProps) {
  // Data as sculpture; living corporate intelligence
  // Data turbulence, point clouds, fluid lattices
  
  return (
    <g>
      <rect x="0" y="0" width="794" height="1123" fill={brand.deep} />
      
      {/* Point cloud - data particles */}
      {Array.from({ length: 400 }).map((_, i) => {
        const angle = (i / 400) * Math.PI * 8;
        const radius = 50 + (i % 100) * 2;
        const turbulence = Math.sin(i * 0.1) * 20 + Math.cos(i * 0.15) * 15;
        const cx = 397 + Math.cos(angle) * (radius + turbulence);
        const cy = 300 + Math.sin(angle) * (radius + turbulence);
        const size = 1 + Math.sin(i * 0.3) * 1.5;
        const colors = [brand.aqua, brand.violet, brand.amber, brand.white];
        const color = colors[i % 4];
        
        return (
          <circle
            key={`point-${i}`}
            cx={cx}
            cy={cy}
            r={size}
            fill={color}
            opacity={0.7}
          />
        );
      })}
      
      {/* Fluid lattice - flowing data structure */}
      {Array.from({ length: 20 }).map((_, row) =>
        Array.from({ length: 28 }).map((_, col) => {
          const x = 60 + col * 26;
          const y = 580 + row * 26;
          const wave = Math.sin(col * 0.3 + row * 0.2) * 8;
          const flow = Math.cos(col * 0.2 - row * 0.3) * 6;
          
          return (
            <circle
              key={`lattice-${row}-${col}`}
              cx={x + wave}
              cy={y + flow}
              r={2 + Math.abs(wave) * 0.3}
              fill={Math.abs(wave) > 4 ? brand.aqua : brand.violet}
              opacity={0.5 + Math.abs(wave) * 0.05}
            />
          );
        })
      )}
      
      {/* Data turbulence field */}
      {Array.from({ length: 60 }).map((_, i) => {
        const angle1 = (i / 60) * Math.PI * 2;
        const angle2 = ((i + 1) / 60) * Math.PI * 2;
        const r1 = 180;
        const r2 = 240;
        const turbulence1 = Math.sin(i * 0.5) * 30;
        const turbulence2 = Math.cos(i * 0.7) * 25;
        
        return (
          <line
            key={`turbulence-${i}`}
            x1={397 + Math.cos(angle1) * (r1 + turbulence1)}
            y1={300 + Math.sin(angle1) * (r1 + turbulence1)}
            x2={397 + Math.cos(angle2) * (r2 + turbulence2)}
            y2={300 + Math.sin(angle2) * (r2 + turbulence2)}
            stroke={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
            strokeWidth="0.5"
            opacity={0.4}
          />
        );
      })}
      
      {/* Vasarely optical grid overlay on data */}
      {Array.from({ length: 10 }).map((_, i) => {
        const size = 240 - i * 24;
        const offset = i * 12;
        return (
          <rect
            key={`optical-${i}`}
            x={277 + offset}
            y={180 + offset}
            width={size}
            height={size}
            fill="none"
            stroke={brand.white}
            strokeWidth="0.5"
            opacity={0.1}
          />
        );
      })}
      
      {/* Flowing data streams */}
      {Array.from({ length: 8 }).map((_, i) => {
        const startX = 80 + i * 90;
        const path = Array.from({ length: 40 }).map((_, j) => {
          const x = startX + j * 2;
          const y = 100 + Math.sin(j * 0.2 + i * 0.5) * 40;
          return `${j === 0 ? "M" : "L"} ${x},${y}`;
        }).join(" ");
        
        return (
          <path
            key={`stream-${i}`}
            d={path}
            fill="none"
            stroke={brand.aqua}
            strokeWidth="1"
            opacity={0.3}
          />
        );
      })}
      
      {/* Sculptural data forms - bottom section */}
      {Array.from({ length: 30 }).map((_, i) => {
        const x = 80 + (i % 15) * 45;
        const y = 950 + Math.floor(i / 15) * 70;
        const height = 40 + Math.sin(i * 0.4) * 25;
        
        return (
          <rect
            key={`sculpture-${i}`}
            x={x}
            y={y - height}
            width={36}
            height={height}
            fill={i % 3 === 0 ? brand.aqua : i % 3 === 1 ? brand.violet : brand.amber}
            opacity={0.6}
          />
        );
      })}
      
      {/* Connecting neural-like network */}
      {Array.from({ length: 50 }).map((_, i) => {
        const seed = i * 45.678;
        const x1 = 100 + (Math.sin(seed) * 300 + 300);
        const y1 = 600 + (Math.cos(seed * 1.3) * 150 + 150);
        const x2 = 100 + (Math.sin(seed * 1.5) * 300 + 300);
        const y2 = 600 + (Math.cos(seed * 2) * 150 + 150);
        
        return (
          <line
            key={`neural-${i}`}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={brand.violet}
            strokeWidth="0.3"
            opacity={0.15}
          />
        );
      })}
    </g>
  );
}
